import { useEffect, useMemo, useState } from "react";
import { pathLabel, thumbPath } from "@iltocat/shared";
import { api, plural } from "../lib/api.js";
import { useItems } from "../lib/useItems.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { LocationChooser } from "../components/LocationChooser.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";

const UNFILED = { locationId: null } as const;

/**
 * Sorting the unfiled pile, one thing at a time.
 *
 * A capture binge ends with a pile of items that have photos and no home.
 * Ticking them and moving them in batches means deciding which ones go
 * together from thumbnails; this shows one, big, with the places it most
 * likely goes as one-tap buttons, and the next one appears as soon as it is
 * filed. Skip leaves it unfiled for later; Undo puts the last one back.
 *
 * Location counts are read again when the screen is left rather than after
 * every item: nothing here shows them, and it is a round trip per tap.
 */
export function Sort({
  taxonomy,
  onOpen,
  onBack,
}: {
  taxonomy: Taxonomy;
  onOpen: (itemId: string) => void;
  onBack: () => void;
}) {
  const list = useItems(UNFILED);
  const [filed, setFiled] = useState<{ id: string; title: string; to: string }[]>([]);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { reload } = taxonomy;
  useEffect(() => () => void reload(), [reload]);

  const filedIds = useMemo(() => new Set(filed.map((entry) => entry.id)), [filed]);
  const waiting = list.items.filter((item) => !filedIds.has(item.id) && !skipped.has(item.id));
  const item = waiting[0] ?? null;
  const left = Math.max(list.total - filed.length, 0);

  // The next page is read before the loaded one runs out.
  const { more, loadingMore, showMore } = list;
  useEffect(() => {
    if (waiting.length < 3 && more && !loadingMore) showMore();
  }, [waiting.length, more, loadingMore, showMore]);

  async function file(to: string | null) {
    if (!item || !to) return;
    setWorking(true);
    setError(null);
    try {
      await api.items.update(item.id, { locationId: to });
      taxonomy.remember(to, null);
      setFiled((current) => [...current, { id: item.id, title: item.title || "Untitled item", to }]);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That item could not be moved.");
    } finally {
      setWorking(false);
    }
  }

  async function undo() {
    const last = filed.at(-1);
    if (!last) return;
    setWorking(true);
    setError(null);
    try {
      await api.items.update(last.id, { locationId: null });
      setFiled((current) => current.slice(0, -1));
      // Back at the front of the queue, where it was.
      setSkipped((current) => {
        const next = new Set(current);
        next.delete(last.id);
        return next;
      });
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That item could not be put back.");
    } finally {
      setWorking(false);
    }
  }

  const last = filed.at(-1);
  const lastWhere = last ? taxonomy.byId.get(last.to) : undefined;

  return (
    <>
      <PageHeader
        back={{ label: "Locations", onClick: onBack }}
        title="Sort unfiled items"
        meta={
          list.loading ? undefined : <span>{left > 0 ? `${plural(left, "item")} to go` : "All done"}</span>
        }
      />

      {list.error && <Notice tone="error">{list.error}</Notice>}
      {error && <Notice tone="error">{error}</Notice>}

      {last && (
        <div className="sorter-last" aria-live="polite">
          <Icon name="check-circle" size={16} />
          <span>{`${last.title} → ${lastWhere ? pathLabel(lastWhere.path) : "a location"}`}</span>
          <button
            type="button"
            className="button button-quiet button-small"
            disabled={working}
            onClick={() => void undo()}
          >
            Undo
          </button>
        </div>
      )}

      {list.loading ? (
        <div className="sorter-card card-skeleton" aria-busy="true" aria-label="Loading" />
      ) : item ? (
        <div className="sorter">
          <article className="sorter-card">
            <button
              type="button"
              className="sorter-shot"
              onClick={() => onOpen(item.id)}
              aria-label="Open this item"
            >
              {item.coverPhotoId ? (
                <img src={thumbPath(item.coverPhotoId)} alt="" decoding="async" />
              ) : (
                <span className="card-noshot" aria-hidden="true">
                  <Icon name="image" size={36} />
                </span>
              )}
            </button>
            <div className="sorter-body">
              <h2 className={item.needsDetails ? "sorter-title card-untitled" : "sorter-title"}>
                {item.title || "Needs details"}
                {item.quantity > 1 && <span className="card-qty"> × {item.quantity}</span>}
              </h2>
              <button
                type="button"
                className="button button-quiet button-small"
                disabled={working}
                onClick={() => setSkipped((current) => new Set([...current, item.id]))}
              >
                Skip for now
                <Icon name="chevron-right" size={16} />
              </button>
            </div>
          </article>

          <div className="sorter-where">
            <h2 className="section-title">Where does it go?</h2>
            <LocationChooser
              key={item.id}
              taxonomy={taxonomy}
              here={null}
              offerUnfiled={false}
              disabled={working}
              onChoose={(to) => void file(to)}
            />
          </div>
        </div>
      ) : skipped.size > 0 && !list.more ? (
        <EmptyState
          icon="clock"
          title={`${plural(skipped.size, "item")} skipped`}
          action={
            <button type="button" className="button" onClick={() => setSkipped(new Set())}>
              Go through them again
            </button>
          }
        >
          Everything else has a home.
        </EmptyState>
      ) : list.loadingMore ? (
        <div className="sorter-card card-skeleton" aria-busy="true" aria-label="Loading" />
      ) : (
        <EmptyState
          icon="check-circle"
          title="Everything has a home"
          action={
            <button type="button" className="button" onClick={onBack}>
              Back to locations
            </button>
          }
        >
          {filed.length > 0
            ? `${plural(filed.length, "item")} filed this time.`
            : "Nothing is waiting to be filed."}
        </EmptyState>
      )}
    </>
  );
}
